import React from 'react';

import {
  AccountBalance as AccountBalanceIcon,
  ManageAccounts as ManageAccountsIcon,
  PeopleAlt as PeopleAltIcon,
  Settings as SettingsIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { ItemDetailsLayout } from 'src/components/common';
import { usePeoById, usePeoParamId } from 'src/hooks/peo';
import { useCurrentUser } from 'src/hooks/user/useCurrentUser';
import { MenuItem } from 'src/types/menu';
import { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

export const PeosDetailLayoutPeoPayrollProviderStaff: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const peoId = usePeoParamId();
  const { peo, updatePeoLogo, loading: peoLoading } = usePeoById(peoId);

  const { isPartnershipCompanyUser, isUserUpdating } = useCurrentUser();

  const builtRoutes = React.useMemo(
    () => ({
      companyInformation: buildUrl(appRoutes.peos.detail.companyInformation, {
        [RouterEntitiesId.peo]: peoId,
      }),
      employers: buildUrl(appRoutes.peos.detail.employers, {
        [RouterEntitiesId.peo]: peoId,
      }),
      staff: buildUrl(appRoutes.peos.detail.staff, {
        [RouterEntitiesId.peo]: peoId,
      }),
      employees: buildUrl(appRoutes.peos.detail.employees, {
        [RouterEntitiesId.peo]: peoId,
      }),
      settings: buildUrl(appRoutes.peos.detail.settings, {
        [RouterEntitiesId.peo]: peoId,
      }),
    }),
    [peoId],
  );

  const menuItems: Array<MenuItem> = React.useMemo(
    () => [
      {
        link: builtRoutes.companyInformation,
        icon: AccountBalanceIcon,
        title: 'Company Information',
      },
      {
        link: builtRoutes.employers,
        icon: WorkIcon,
        title: 'Employers under PEO',
      },
      {
        link: builtRoutes.staff,
        icon: ManageAccountsIcon,
        title: 'Staff',
      },
      {
        link: builtRoutes.employees,
        icon: PeopleAltIcon,
        title: 'Employees',
        hide: isPartnershipCompanyUser,
      },
      {
        link: builtRoutes.settings,
        icon: SettingsIcon,
        title: 'Settings',
      },
    ],
    [builtRoutes, isPartnershipCompanyUser],
  );

  return (
    <ItemDetailsLayout
      name={peo?.displayName}
      subtitle="PEO"
      logoUrl={peo?.logo?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updatePeoLogo,
        loading: peoLoading,
      }}
      loading={peoLoading || isUserUpdating}
    >
      <Box sx={childrenContainerStyles}>{children}</Box>
    </ItemDetailsLayout>
  );
};

const childrenContainerStyles: SxProp = theme => ({
  [theme.breakpoints.up('sm')]: {
    minHeight: '80vh',
  },
});
